/**
 * As checagens do laudo do @DevOps: o que foi verificado (com veredito) e "O que eu não
 * verifiquei". Saíram do `DiagnosticoCard` para o painel do @DevOps (spec
 * 2026-11-04-painel-do-devops §3.3) mostrar a conferência do pedido com a MESMA cara do
 * laudo — uma leitura, uma aparência. O laudo segue renderizando os dois blocos nas
 * mesmas posições de antes.
 */
import { useState } from 'react'
import { Button, Group, Stack, Text } from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconEyeOff,
  IconX,
} from '@tabler/icons-react'

import { FERRAMENTAS_COM_ROTULO, friendlyToolLabel } from '../toolLabels'

export type Veredito = 'ok' | 'falha' | 'suspeito' | 'nao_verificavel'

export interface ChecagemLaudo {
  id: string
  titulo: string
  veredito: Veredito
  observado: string
  motivo_nao_verificavel?: string | null
  detalhe_interno?: Record<string, unknown>
}

/** Título da checagem: a leitura com rótulo próprio (`banco.colunas`) sai legível; o resto,
 *  que já é título de receita, como veio. */
function tituloDaChecagem(titulo: string): string {
  return FERRAMENTAS_COM_ROTULO.includes(titulo) ? friendlyToolLabel(titulo) : titulo
}

// ── Aparência por veredito ─────────────────────────────────────────────────
//
// Cores do design system: `inovareTeal` é a marca e marca o que está certo —
// não um verde genérico. Vermelho e amarelo ficam para falha e suspeita, que é
// o uso semântico que o resto do chat já faz.
const APARENCIA: Record<Veredito, { cor: string; Icone: typeof IconCheck; rotulo: string }> = {
  ok: { cor: 'inovareTeal', Icone: IconCheck, rotulo: 'ok' },
  falha: { cor: 'red', Icone: IconX, rotulo: 'falha' },
  suspeito: { cor: 'yellow', Icone: IconAlertTriangle, rotulo: 'atenção' },
  nao_verificavel: { cor: 'gray', Icone: IconEyeOff, rotulo: 'não verificado' },
}

// Saída de uma leitura da investigação livre (`detalhe_interno.saida`, só
// para quem tem a role que enxerga identificador de infra — o backend já
// stripa o campo para os demais, e o cartão apenas renderiza quando presente,
// nunca deriva; mesma disciplina de `alvo_exibido.detalhe_interno`).
//
// Recolhível e FORA do texto de `observado`: a saída do host pode ser longa
// (até 4000 caracteres, `investigacao.py:_checagem`) e o cartão é o chão de
// fatos — `observado` já diz o essencial redigido; a saída crua é para quem
// quer conferir, não para quem só quer ler o laudo.
function SaidaDaLeitura({ id, saida }: { id: string; saida: string }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div>
      <Button
        variant="subtle"
        color="gray"
        size="compact-xs"
        px={0}
        leftSection={aberto ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
        onClick={() => setAberto((v) => !v)}
        data-saida-toggle={id}
      >
        {aberto ? 'ocultar saída' : 'ver saída'}
      </Button>
      {aberto && (
        <Text
          component="pre"
          size="xs"
          c="dimmed"
          data-saida={id}
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {saida}
        </Text>
      )}
    </div>
  )
}

/** As checagens com veredito (ok, falha, atenção). As `nao_verificavel` ficam de fora:
 *  são de `NaoVerificadas`. */
export function ChecagensVerificadas({ checagens }: { checagens: ChecagemLaudo[] }) {
  const verificadas = checagens.filter((c) => c.veredito !== 'nao_verificavel')
  return (
    <Stack gap={4}>
      {verificadas.map((c) => {
        const { cor, Icone, rotulo } = APARENCIA[c.veredito] ?? APARENCIA.nao_verificavel
        const saida = c.detalhe_interno?.saida
        return (
          <Group key={c.id} gap={6} wrap="nowrap" align="flex-start" data-veredito={c.veredito}>
            <Icone size={14} color={`var(--mantine-color-${cor}-6)`} style={{ marginTop: 2 }} />
            <div>
              <Text size="xs" fw={500}>
                {tituloDaChecagem(c.titulo)}{' '}
                <Text span size="xs" c="dimmed">
                  · {rotulo}
                </Text>
              </Text>
              {c.observado && (
                <Text size="xs" c="dimmed">
                  {c.observado}
                </Text>
              )}
              {typeof saida === 'string' && saida && (
                <SaidaDaLeitura id={c.id} saida={saida} />
              )}
            </div>
          </Group>
        )
      })}
    </Stack>
  )
}

/** "O que eu não verifiquei": as checagens `nao_verificavel` e, no parecer do @DBA, o texto
 *  do que não foi lido. Visível, e não atrás de "detalhes": é o que impede o laudo de
 *  parecer completo quando não é. Sem nada a dizer, não renderiza. */
export function NaoVerificadas({ checagens, textos = [] }: {
  checagens: ChecagemLaudo[]
  textos?: string[]
}) {
  const naoVerificadas = checagens.filter((c) => c.veredito === 'nao_verificavel')
  if (naoVerificadas.length === 0 && textos.length === 0) return null
  return (
    <Stack gap={4} mt="sm">
      <Text size="xs" fw={600} c="dimmed">
        O que eu não verifiquei
      </Text>
      {naoVerificadas.map((c) => (
        <Group key={c.id} gap={6} wrap="nowrap" align="flex-start" data-veredito={c.veredito}>
          <IconEyeOff size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
          <div>
            <Text size="xs" fw={500}>{c.titulo}</Text>
            {c.motivo_nao_verificavel && (
              <Text size="xs" c="dimmed">{c.motivo_nao_verificavel}</Text>
            )}
          </div>
        </Group>
      ))}
      {textos.map((t, i) => (
        <Group key={`texto-${i}`} gap={6} wrap="nowrap" align="flex-start">
          <IconEyeOff size={14} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
          <Text size="xs" c="dimmed">{t}</Text>
        </Group>
      ))}
    </Stack>
  )
}
